import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Dumbbell, Zap, Trophy, HeartPulse, Music, Shield, Flower2, ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { useSiteContent } from "@/hooks/useContent";

const ICON_MAP = { Dumbbell, Zap, Trophy, HeartPulse, Music, Shield, Flower2 };
const ICON_FALLBACK = [Dumbbell, Zap, Trophy, HeartPulse, Music, Shield, Flower2];

function ServiceCard({ service, index }) {
  const [expanded, setExpanded] = useState(false);
  const { content } = useSiteContent();
  const wa = content?.settings?.whatsapp || "919153020904";
  const Icon = ICON_MAP[service.icon] || ICON_FALLBACK[index % ICON_FALLBACK.length];

  const bookOnWhatsApp = (e) => {
    e.stopPropagation();
    const msg = encodeURIComponent(`Hi! I'm interested in ${service.name} under your Personal Training program. Can you share more details?`);
    window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -5, scale: 1.02 }}
      onClick={() => setExpanded(!expanded)}
      className="border border-white/10 rounded-lg group relative overflow-hidden bg-gg-black cursor-pointer hover-glow transition-shadow duration-300 flex flex-col" data-testid={`service-card-${index}`}>
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-gg-surface">
        {service.image ? <img src={service.image} alt={service.name} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700" />
          : <div className="w-full h-full flex items-center justify-center"><Icon className="w-16 h-16 text-gg-red/20 group-hover:text-gg-red/40 group-hover:scale-110 transition-all duration-500" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-gg-black via-gg-black/40 to-transparent" />
        <div className="absolute top-3 right-3"><motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.3 }}><ChevronDown size={16} className="text-white/50" /></motion.div></div>
        <div className="absolute bottom-3 left-4"><Icon className="w-5 h-5 text-gg-red" /></div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-heading text-sm uppercase tracking-wider font-bold text-white group-hover:text-gg-red transition-colors duration-300">{service.name}</h3>
        <p className="text-zinc-500 text-xs mt-1.5 tracking-wide leading-relaxed flex-1">{service.desc}</p>
        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
              <p className="text-zinc-400 text-xs mt-2 leading-relaxed border-t border-white/5 pt-2">{service.detail}</p>
              <button onClick={bookOnWhatsApp} data-testid={`book-service-${index}`} className="mt-2 flex items-center gap-2 text-xs font-heading uppercase tracking-widest text-gg-red hover:text-white transition-colors group/btn">
                <MessageCircle size={14} /> Book This Session <ArrowRight size={12} className="group-hover/btn:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gg-red group-hover:w-full transition-all duration-500 rounded-full" />
    </motion.div>
  );
}

export default function PersonalTrainingPage() {
  const { content } = useSiteContent();
  const services = content?.services || [];
  const wa = content?.settings?.whatsapp || "919153020904";

  return (
    <div data-testid="personal-training-page">
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-heading text-gg-red text-xs uppercase tracking-[0.4em]">What I Offer</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mt-1">Personal <span className="text-gg-red">Training</span></motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-zinc-500 text-sm mt-2 max-w-xl tracking-wide leading-relaxed">Comprehensive one-on-one fitness programs. Click any specialization to learn more.</motion.p>
        </div>
      </div>
      <section className="py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {services.map((s, i) => <ServiceCard key={i} service={s} index={i} />)}
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap gap-4 items-center">
            <a href={`https://wa.me/${wa}?text=Hi!%20I'm%20interested%20in%20Personal%20Training.`} target="_blank" rel="noopener noreferrer" data-testid="services-book-cta" className="inline-flex items-center gap-2 bg-gg-red hover:bg-gg-red-hover text-white px-6 py-3 rounded-lg font-heading text-xs uppercase tracking-widest transition-all duration-300 hover:shadow-lg hover:shadow-gg-red/20 whitespace-nowrap"><MessageCircle size={16} className="shrink-0" /> Book a Free Consultation <ArrowRight size={14} className="shrink-0" /></a>
            <Link to="/contact" data-testid="services-contact-link" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-heading uppercase tracking-widest transition-colors whitespace-nowrap">Or send me a message <ArrowRight size={14} className="shrink-0" /></Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
